import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { videosApi } from "@/api/videos.ts";
import { PageContainer, TfCard, Btn, Icon, TfSpinner } from "@/components/v2/primitives.tsx";
import type { ContentProfile } from "@vms/tubeforge-shared";

const PROFILE_KEY = "tf_content_profile";

function loadSavedProfile(): Partial<ContentProfile> {
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }  // catch-reason: a saved value that isn't JSON is absent
}

interface Strategy {
  businessAnalysis: {
    corePurpose: string;
    uniqueValueProp: string;
    transformation: { before: string; after: string };
    objections: string[];
    customerJourney: Array<{ stage: string; description: string; contentGoal: string }>;
    brandVoice: string[];
    competitivePosition: string;
  };
  segments: Array<{
    name: string; demographics: string; psychographics: string;
    primaryPainPoint: string; emotionalState: string; buyingTrigger: string;
    primaryObjection: string; mediaHabits: string;
    marketSize: number; conversionPotential: number;
  }>;
  angles: Array<{
    name: string; awarenessStage: string; message: string;
    emotionalLever: string; hookType: string; desiredOutcome: string;
    videoStyle: string;
  }>;
  matrix: Array<{
    segmentIndex: number; angleIndex: number; priority: number;
    videoConcept: string; rationale: string;
  }>;
  publishingStrategy: string;
}

type Phase = "profile" | "analyzing" | "results";

export function StrategyPage() {
  const navigate = useNavigate();
  const saved = loadSavedProfile();

  const [phase, setPhase] = useState<Phase>("profile");
  const [strategy, setStrategy] = useState<Strategy | null>(null);
  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set());

  // Load saved strategies
  const { data: savedStrategies } = useQuery({
    queryKey: ["strategies"],
    queryFn: () => videosApi.listStrategies(),
  });

  // Profile fields
  const [channelNiche, setChannelNiche] = useState(saved.channelNiche || "");
  const [targetAudience, setTargetAudience] = useState(saved.targetAudience || "");
  const [callToAction, setCallToAction] = useState(saved.callToAction || "");
  const [videoGoal, setVideoGoal] = useState<ContentProfile["videoGoal"]>(saved.videoGoal || "grow_audience");
  const [platform, setPlatform] = useState<ContentProfile["platform"]>(saved.platform || "youtube");
  const [productName, setProductName] = useState(saved.productName || "");
  const [differentiator, setDifferentiator] = useState(saved.differentiator || "");

  const strategyMutation = useMutation({
    mutationFn: () => {
      setPhase("analyzing");
      return videosApi.generateStrategy({
        channelNiche: channelNiche || "General",
        targetAudience: targetAudience || "General audience",
        callToAction: callToAction || "Follow for more",
        videoGoal,
        platform,
        productName: productName || undefined,
        differentiator: differentiator || undefined,
      });
    },
    onSuccess: (data) => {
      setStrategy(data);
      setPhase("results");
      // Auto-select top 5 priority cells
      const top = (data.matrix || []).slice(0, 5);
      setSelectedCells(new Set(top.map((c: any) => `${c.segmentIndex}-${c.angleIndex}`)));
    },
    onError: () => setPhase("profile"),
  });

  // ── Profile input phase ──
  if (phase === "profile") {
    return (
      <PageContainer title="Content Strategy" subtitle="Deep business analysis → market segments → prioritized video plan" maxWidth={720}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <TfCard padding={24}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14, color: "var(--tf-fg)" }}>Tell us about your business</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <InputRow label="Niche / Industry" value={channelNiche} onChange={setChannelNiche} placeholder="e.g. Personal finance for millennials" />
              <InputRow label="Target audience" value={targetAudience} onChange={setTargetAudience} placeholder="e.g. 25-35 year olds struggling with student debt" />
              <InputRow label="Product / Service (optional)" value={productName} onChange={setProductName} placeholder="e.g. BudgetMaster app" />
              <InputRow label="What makes you different?" value={differentiator} onChange={setDifferentiator} placeholder="e.g. Only tool that syncs with all major banks" />
              <InputRow label="Desired viewer action" value={callToAction} onChange={setCallToAction} placeholder="e.g. Download the free app" />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label className="tf-label" style={{ display: "block", marginBottom: 6, fontSize: 11 }}>Goal</label>
                  <select className="tf-input" value={videoGoal} onChange={e => setVideoGoal(e.target.value as any)}
                    style={{ width: "100%", padding: "8px 10px" }}>
                    <option value="grow_audience">Grow audience</option>
                    <option value="sell_product">Sell product</option>
                    <option value="build_authority">Build authority</option>
                    <option value="entertain">Entertain</option>
                    <option value="educate">Educate</option>
                  </select>
                </div>
                <div>
                  <label className="tf-label" style={{ display: "block", marginBottom: 6, fontSize: 11 }}>Platform</label>
                  <select className="tf-input" value={platform} onChange={e => setPlatform(e.target.value as any)}
                    style={{ width: "100%", padding: "8px 10px" }}>
                    <option value="youtube">YouTube</option>
                    <option value="tiktok">TikTok</option>
                    <option value="instagram">Instagram</option>
                    <option value="general">General</option>
                  </select>
                </div>
              </div>
            </div>
          </TfCard>
          <Btn size="lg" icon="sparkle" onClick={() => strategyMutation.mutate()}
            disabled={!channelNiche.trim()} loading={strategyMutation.isPending}>
            Analyze & Build Strategy
          </Btn>

          {/* Saved strategies */}
          {savedStrategies?.strategies && savedStrategies.strategies.length > 0 && (
            <TfCard padding={20}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10, color: "var(--tf-fg)" }}>Saved Strategies</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {savedStrategies.strategies.map((s: any) => (
                  <button key={s.id} type="button" onClick={() => {
                    setStrategy(s.strategyJson);
                    setPhase("results");
                    const top = (s.strategyJson.matrix || []).slice(0, 5);
                    setSelectedCells(new Set(top.map((c: any) => `${c.segmentIndex}-${c.angleIndex}`)));
                  }} style={{
                    padding: "10px 14px", borderRadius: 8, cursor: "pointer", textAlign: "left",
                    border: "1px solid var(--tf-rule)", background: "var(--tf-surface)",
                    fontFamily: "inherit", color: "var(--tf-fg)", display: "flex", justifyContent: "space-between", alignItems: "center",
                  }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{s.name}</div>
                      <div className="tf-muted" style={{ fontSize: 11, marginTop: 2 }}>
                        {s.channelNiche} · {new Date(s.updatedAt).toLocaleDateString()}
                      </div>
                    </div>
                    <Icon name="arrowRight" size={14} color="var(--tf-fg-muted)" />
                  </button>
                ))}
              </div>
            </TfCard>
          )}
          {strategyMutation.error && (
            <div style={{ padding: 12, borderRadius: 8, background: "var(--tf-danger-soft)", color: "var(--tf-danger)", fontSize: 13 }}>
              {(strategyMutation.error as any)?.message || "Strategy generation failed"}
            </div>
          )}
        </div>
      </PageContainer>
    );
  }

  // ── Analyzing phase ──
  if (phase === "analyzing") {
    return (
      <PageContainer title="Content Strategy" maxWidth={720}>
        <TfCard padding={40}>
          <div style={{ textAlign: "center" }}>
            <TfSpinner size={32} />
            <div style={{ marginTop: 16, fontSize: 14, fontWeight: 600, color: "var(--tf-fg)" }}>
              Analyzing your business…
            </div>
            <div className="tf-muted" style={{ marginTop: 6, fontSize: 12 }}>
              Mapping market segments, content angles, and building your strategy matrix.
              This takes 15-30 seconds.
            </div>
          </div>
        </TfCard>
      </PageContainer>
    );
  }

  // ── Results phase ──
  if (!strategy) return null;
  const { businessAnalysis: ba, segments, angles, matrix } = strategy;
  const transformation = ba.transformation || { before: "—", after: "—" };
  const objections = ba.objections || [];
  const customerJourney = ba.customerJourney || [];
  const brandVoice = ba.brandVoice || [];

  return (
    <PageContainer title="Content Strategy" subtitle={ba.corePurpose} maxWidth={1000}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

        {/* Business Analysis */}
        <TfCard padding={20}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: "var(--tf-fg)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
            Business Analysis
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <InfoBlock label="Value Proposition" text={ba.uniqueValueProp} />
            <InfoBlock label="Competitive Position" text={ba.competitivePosition} />
            <InfoBlock label="Transformation" text={`"${transformation.before}" → "${transformation.after}"`} />
            <InfoBlock label="Brand Voice" text={brandVoice.join(", ")} />
          </div>
          <div style={{ marginTop: 12 }}>
            <div className="tf-label" style={{ marginBottom: 6 }}>Key objections to address</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {objections.map((o, i) => (
                <span key={i} style={{ padding: "4px 10px", borderRadius: 6, fontSize: 11, background: "var(--tf-surface)", border: "1px solid var(--tf-rule)", color: "var(--tf-fg)" }}>
                  {o}
                </span>
              ))}
            </div>
          </div>
        </TfCard>

        {/* Customer Journey */}
        <TfCard padding={20}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: "var(--tf-fg)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
            Customer Journey
          </div>
          <div style={{ display: "flex", gap: 2 }}>
            {customerJourney.map((step, i) => (
              <div key={i} style={{
                flex: 1, padding: 12, borderRadius: 8,
                background: "var(--tf-surface)", border: "1px solid var(--tf-rule)",
              }}>
                <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "var(--tf-accent)", letterSpacing: "0.06em", marginBottom: 4 }}>
                  {step.stage.replace(/_/g, " ")}
                </div>
                <div style={{ fontSize: 11, color: "var(--tf-fg)", lineHeight: 1.4 }}>{step.description}</div>
                <div className="tf-muted" style={{ fontSize: 10, marginTop: 4 }}>Goal: {step.contentGoal}</div>
              </div>
            ))}
          </div>
        </TfCard>

        {/* Segments */}
        <TfCard padding={20}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: "var(--tf-fg)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
            Market Segments
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: 10 }}>
            {segments.map((seg, i) => (
              <div key={i} style={{
                padding: 14, borderRadius: 10, border: "1px solid var(--tf-rule)",
                background: "var(--tf-surface)",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--tf-fg)" }}>{seg.name}</div>
                  <div style={{ display: "flex", gap: 4 }}>
                    <ScoreBadge label="Size" value={seg.marketSize} />
                    <ScoreBadge label="Conv" value={seg.conversionPotential} />
                  </div>
                </div>
                <div style={{ fontSize: 11, color: "var(--tf-fg-muted)", lineHeight: 1.5 }}>
                  <strong>Pain:</strong> {seg.primaryPainPoint}
                </div>
                <div style={{ fontSize: 11, color: "var(--tf-fg-muted)", lineHeight: 1.5, marginTop: 2 }}>
                  <strong>Trigger:</strong> {seg.buyingTrigger}
                </div>
                <div style={{ fontSize: 11, color: "var(--tf-fg-muted)", lineHeight: 1.5, marginTop: 2 }}>
                  <strong>Objection:</strong> {seg.primaryObjection}
                </div>
              </div>
            ))}
          </div>
        </TfCard>

        {/* Strategy Matrix */}
        <TfCard padding={20}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--tf-fg)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Strategy Matrix
              </div>
              <div className="tf-muted" style={{ fontSize: 11, marginTop: 2 }}>
                Select which videos to produce — sorted by impact
              </div>
            </div>
            <div className="tf-muted" style={{ fontSize: 12 }}>
              {selectedCells.size} selected
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {matrix.map((cell, i) => {
              const seg = segments[cell.segmentIndex];
              const angle = angles[cell.angleIndex];
              if (!seg || !angle) return null;
              const key = `${cell.segmentIndex}-${cell.angleIndex}`;
              const selected = selectedCells.has(key);
              return (
                <button key={i} type="button" onClick={() => {
                  setSelectedCells(prev => {
                    const next = new Set(prev);
                    if (next.has(key)) next.delete(key); else next.add(key);
                    return next;
                  });
                }} style={{
                  padding: "12px 14px", borderRadius: 10, cursor: "pointer", textAlign: "left",
                  border: selected ? "2px solid var(--tf-accent)" : "1px solid var(--tf-rule)",
                  background: selected ? "var(--tf-accent-soft)" : "var(--tf-surface)",
                  display: "flex", gap: 12, alignItems: "flex-start", transition: "all .15s",
                  fontFamily: "inherit", color: "var(--tf-fg)",
                }}>
                  {/* Priority badge */}
                  <div style={{
                    width: 28, height: 28, borderRadius: 6, flexShrink: 0,
                    background: cell.priority >= 8 ? "var(--tf-accent)" : cell.priority >= 5 ? "var(--tf-fg-muted)" : "var(--tf-rule)",
                    color: cell.priority >= 5 ? "#fff" : "var(--tf-fg)",
                    display: "grid", placeItems: "center", fontSize: 12, fontWeight: 700,
                  }}>
                    {cell.priority}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{cell.videoConcept}</div>
                    <div style={{ display: "flex", gap: 6, marginTop: 4, flexWrap: "wrap" }}>
                      <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: 10, background: "var(--tf-accent-soft)", color: "var(--tf-accent)", fontWeight: 600 }}>
                        {seg.name}
                      </span>
                      <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: 10, background: "var(--tf-surface)", border: "1px solid var(--tf-rule)", color: "var(--tf-fg-muted)" }}>
                        {angle.name} · {angle.awarenessStage.replace(/_/g, " ")}
                      </span>
                    </div>
                    <div className="tf-muted" style={{ fontSize: 10, marginTop: 4 }}>{cell.rationale}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </TfCard>

        {/* Publishing strategy */}
        <TfCard padding={16}>
          <div className="tf-label" style={{ marginBottom: 6 }}>Publishing Strategy</div>
          <div style={{ fontSize: 12, color: "var(--tf-fg)", lineHeight: 1.6 }}>{strategy.publishingStrategy}</div>
        </TfCard>

        {/* (TF3-3) "Generate N videos" (POST /videos/campaign: legacy videos made outside the engine) is retired */}
        <TfCard padding={20}>
          <div style={{ fontSize: 12, color: "var(--tf-fg-muted)", marginBottom: 12 }}>
            Make each video from New Video, with this strategy in your guidance.
          </div>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <Btn icon="plus" onClick={() => navigate("/videos/new")}>New video</Btn>
            <Btn variant="ghost" onClick={() => { setPhase("profile"); setStrategy(null); }}>
              Start over
            </Btn>
          </div>
        </TfCard>
      </div>
    </PageContainer>
  );
}

// ── Sub-components ──

function InputRow({ label, value, onChange, placeholder }: {
  label: string; value: string; onChange: (v: string) => void; placeholder: string;
}) {
  return (
    <div>
      <label className="tf-label" style={{ display: "block", marginBottom: 6, fontSize: 11 }}>{label}</label>
      <input className="tf-input" value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} style={{ width: "100%" }} />
    </div>
  );
}

function InfoBlock({ label, text }: { label: string; text: string }) {
  return (
    <div>
      <div className="tf-label" style={{ marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 12, color: "var(--tf-fg)", lineHeight: 1.5 }}>{text}</div>
    </div>
  );
}

function ScoreBadge({ label, value }: { label: string; value: number }) {
  const color = value >= 8 ? "var(--tf-accent)" : value >= 5 ? "var(--tf-fg-muted)" : "var(--tf-rule)";
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 13, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 8, color: "var(--tf-fg-muted)", textTransform: "uppercase" }}>{label}</div>
    </div>
  );
}
